import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Building2, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Badge, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { useTenants } from '@/api/queries/platform-admin';
import type { components } from '@/api/generated/schema';

type TenantStatus = components['schemas']['PixlideDomainIdentityEnumsTenantStatus'];

/** Correspondance avec `TenantStatus` côté domaine (`Trialing`, `Active`, `Suspended`, `Cancelled`). */
const STATUS_LABELS: Record<TenantStatus, string> = {
  0: 'Essai',
  1: 'Actif',
  2: 'Suspendu',
  3: 'Résilié',
};

const STATUS_TONES: Record<TenantStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  0: 'neutral',
  1: 'success',
  2: 'warning',
  3: 'danger',
};

const PAGE_SIZE = 20;

/** Couleurs d'avatar cycliques — simple variation visuelle entre lignes, sans signification métier. */
const AVATAR_TONES = [
  'bg-brand-100 text-brand-700',
  'bg-success-100 text-success-500',
  'bg-warning-100 text-warning-500',
  'bg-ink-100 text-ink-700',
];

function avatarTone(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash + seed.charCodeAt(i)) % AVATAR_TONES.length;
  return AVATAR_TONES[hash];
}

/** Initiales du nom d'entreprise, pour l'avatar de la liste (pas de logo tenant côté API). */
function initials(companyName: string) {
  const words = companyName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/** Liste des tenants de la plateforme, tous statuts confondus (recherche + filtre + pagination). */
export function TenantsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  const [page, setPage] = useState(1);

  const tenants = useTenants({
    search: search.trim() === '' ? null : search.trim(),
    status: status === '' ? null : status,
    page,
    pageSize: PAGE_SIZE,
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Tenants"
        description="Comptes clients de la plateforme, tous statuts confondus."
      />

      {/* Filtres groupés à gauche — la page n'a pas d'action de création/export côté API pour
          l'instant (les tenants sont créés par inscription, pas depuis le back-office), donc
          pas de bouton à droite pour équilibrer le rang, contrairement à la référence Figma. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
          />
          <input
            type="search"
            aria-label="Rechercher un tenant"
            placeholder="Nom ou slug…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="h-10 w-64 rounded-pill bg-white pr-4 pl-9 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
          />
        </div>

        <select
          aria-label="Filtrer par statut"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value === '' ? '' : (Number(event.target.value) as TenantStatus));
            setPage(1);
          }}
          className="h-10 rounded-pill bg-white px-4 text-sm ring-1 ring-ink-100"
        >
          <option value="">Tous les statuts</option>
          {(Object.entries(STATUS_LABELS) as [string, string][]).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {tenants.isPending ? <LoadingState label="Chargement des tenants…" /> : null}

      {tenants.isError ? (
        <ErrorState
          message="Impossible de charger la liste des tenants."
          onRetry={() => void tenants.refetch()}
        />
      ) : null}

      {tenants.data?.items.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-10" strokeWidth={1.25} />}
          title="Aucun tenant"
          description="Aucun compte client ne correspond à cette recherche."
        />
      ) : null}

      {tenants.data && tenants.data.items.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Liste des tenants de la plateforme</caption>

              <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                <tr>
                  <th scope="col" className="px-5 py-3">Tenant</th>
                  <th scope="col" className="px-5 py-3">Slug</th>
                  <th scope="col" className="px-5 py-3">Statut</th>
                  <th scope="col" className="px-5 py-3">Créé le</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-ink-100/70">
                {tenants.data.items.map((tenant) => (
                  <tr key={tenant.id}>
                    <th scope="row" className="px-5 py-4 text-left font-medium">
                      <Link
                        to="/tenants/$tenantId"
                        params={{ tenantId: tenant.id }}
                        className="flex items-center gap-3 hover:underline"
                      >
                        <span
                          aria-hidden
                          className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${avatarTone(tenant.id)}`}
                        >
                          {initials(tenant.companyName)}
                        </span>
                        {tenant.companyName}
                      </Link>
                    </th>

                    <td className="px-5 py-4 text-ink-500">{tenant.slug}</td>

                    <td className="px-5 py-4">
                      <Badge tone={STATUS_TONES[tenant.status]}>{STATUS_LABELS[tenant.status]}</Badge>
                    </td>

                    <td className="px-5 py-4 text-ink-500">
                      {new Date(tenant.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-ink-100/70 px-5 py-3 text-sm text-ink-500">
            <span>
              Page {tenants.data.page} sur {Math.max(tenants.data.totalPages, 1)} ·{' '}
              {tenants.data.totalCount} tenant{tenants.data.totalCount > 1 ? 's' : ''}
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!tenants.data.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
                aria-label="Page précédente"
                className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft aria-hidden className="size-4" />
              </button>

              <button
                type="button"
                disabled={!tenants.data.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
                aria-label="Page suivante"
                className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronRight aria-hidden className="size-4" />
              </button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
